import React, { useState } from 'react';

import { css } from '@emotion/css';
import { TenantProductName, TenantUserPermission } from '@recurrency/core-api-schema/dist/common/enums';
import { TenantDTO } from '@recurrency/core-api-schema/dist/tenants/tenantDTO';
import { Checkbox, Collapse } from 'antd';
import { ColumnType } from 'antd/lib/table';
import { colors } from 'theme/colors';
import { fontWeights } from 'theme/typography';

import { Button } from 'components/Button';
import { ConfirmModal } from 'components/Modal/ConfirmModal';
import { Select } from 'components/Select';
import { Table } from 'components/Table';
import { InfoTooltip } from 'components/Tooltip/InfoTooltip';

import { showAsyncModal } from 'utils/asyncModal';
import { shouldShowProduct } from 'utils/roleAndTenant';
import { asKeyOf } from 'utils/tables';

export type PermissionValues = Partial<Record<TenantUserPermission, boolean>>;

interface AccessArea {
  label: string;
  description: string;
  view: TenantUserPermission;
  /** Omitted when the area has nothing to export. */
  download?: TenantUserPermission;
  /** Any one of these must be enabled for the row to show; omitted when every tenant has the area. */
  products?: TenantProductName[];
}

interface AccessSection {
  label: string;
  areas: AccessArea[];
}

const accessSections: AccessSection[] = [
  {
    label: 'Demand Planning',
    areas: [
      {
        label: 'Dashboard',
        description:
          'This permission grants access to the Demand Planning Dashboard and the Historical and Current Inventory Explorers',
        view: TenantUserPermission.AccessPlanningDashboardView,
        download: TenantUserPermission.AccessPlanningDashboardDownload,
        products: [TenantProductName.DfpaMissionControl],
      },
      {
        label: 'Daily Workflow',
        description: 'This permission grants access to the Daily Workflow page and the Daily Workflow Inventory Alert',
        view: TenantUserPermission.AccessPlanningDailyWorkflowView,
        products: [TenantProductName.DfpaDemandPlanning],
      },
      {
        label: 'Forecasting',
        description: 'This permission grants access to the Forecasting page',
        view: TenantUserPermission.AccessPlanningForecastingView,
        download: TenantUserPermission.AccessPlanningForecastingDownload,
        products: [TenantProductName.DfpaDemandPlanning],
      },
      {
        label: 'Planning',
        description: 'This permission grants access to the Planning page and Planning Inventory Alerts',
        view: TenantUserPermission.AccessPlanningPlanningView,
        download: TenantUserPermission.AccessPlanningPlanningDownload,
        products: [TenantProductName.DfpaDemandPlanning],
      },
    ],
  },
  {
    label: 'Purchasing',
    areas: [
      {
        label: 'Purchase Targets',
        description:
          'This permission grants access to the Purchase Targets page and the Purchase Targets Inventory Alert',
        view: TenantUserPermission.AccessPurchasingPurchaseTargetsView,
        products: [TenantProductName.DfpaPurchasingAutomation],
      },
      {
        label: 'Transfer Replenishment',
        description: 'This permission grants access to the Transfer Replenishment page',
        view: TenantUserPermission.AccessPurchasingTransferReplenishmentView,
        products: [TenantProductName.DfpaPurchasingAutomation],
      },
      {
        label: 'Purchase Groups',
        description: 'This permission grants access to the Purchase Groups page',
        view: TenantUserPermission.AccessPurchasingPurchaseGroupsView,
        products: [TenantProductName.DfpaPurchasingAutomation],
      },
      {
        label: 'Purchase Orders',
        description: 'This permission grants access to the Purchase Orders page and purchase order details',
        view: TenantUserPermission.AccessPurchasingPurchaseOrdersView,
        products: [TenantProductName.DfpaSearchAndLookup],
      },
      {
        label: 'Transfer Orders',
        description: 'This permission grants access to the Transfer Orders page and transfer order details',
        view: TenantUserPermission.AccessPurchasingTransferOrdersView,
        products: [TenantProductName.DfpaPurchasingAutomation],
      },
      {
        label: 'Purchase Status',
        description: 'This permission grants access to the Purchase Status page',
        view: TenantUserPermission.AccessPurchasingPurchaseStatusView,
        download: TenantUserPermission.AccessPurchasingPurchaseStatusDownload,
        products: [TenantProductName.DfpaPurchasingAutomation],
      },
      {
        label: 'Vendors',
        description: 'This permission grants access to the Vendors page and vendor details',
        view: TenantUserPermission.AccessPurchasingVendorsView,
        download: TenantUserPermission.AccessPurchasingVendorsDownload,
        products: [TenantProductName.DfpaSearchAndLookup],
      },
      {
        label: 'Inventory',
        description:
          'This permission grants access to the Inventory page and item details under both Purchasing and Sales',
        view: TenantUserPermission.AccessPurchasingInventoryView,
        products: [TenantProductName.DfpaSearchAndLookup, TenantProductName.SalesSearchAndLookup],
      },
    ],
  },
  {
    label: 'Sales',
    areas: [
      {
        label: 'Dashboard',
        description: 'This permission grants access to the Sales Dashboard',
        view: TenantUserPermission.AccessSalesDashboardView,
        products: [TenantProductName.SalesMissionControl],
      },
      {
        label: 'Customers',
        description: 'This permission grants access to the Customers page and customer details',
        view: TenantUserPermission.AccessSalesCustomersView,
        products: [TenantProductName.SalesSearchAndLookup],
      },
    ],
  },
  {
    label: 'Orders',
    areas: [
      {
        label: 'Quotes',
        description: 'This permission grants access to the Quotes and Draft Quotes pages and quote details',
        view: TenantUserPermission.AccessOrdersQuotesView,
        products: [TenantProductName.SalesQuoting],
      },
      {
        label: 'Orders',
        description: 'This permission grants access to the Orders page and order details',
        view: TenantUserPermission.AccessOrdersOrdersView,
        products: [TenantProductName.SalesSearchAndLookup, TenantProductName.DfpaSearchAndLookup],
      },
    ],
  },
  {
    label: 'Sales Reporting',
    areas: [
      {
        label: 'Sales Reporting',
        description: 'This permission grants access to the Explorer and Reports pages and saved reports',
        view: TenantUserPermission.AccessReportingReportingView,
        download: TenantUserPermission.AccessReportingReportingDownload,
        products: [TenantProductName.SalesReporting],
      },
    ],
  },
  {
    label: 'Tasks',
    areas: [
      {
        label: 'Tasks',
        description: 'This permission grants access to the Tasks page and task details',
        view: TenantUserPermission.AccessTasksTasksView,
        download: TenantUserPermission.AccessTasksTasksDownload,
        products: [TenantProductName.SalesCrm],
      },
    ],
  },
  {
    label: 'Rules',
    areas: [
      {
        label: 'Rules',
        description: 'This permission grants access to the Rule Management page and rule details',
        view: TenantUserPermission.AccessRuleBuilderRulesView,
        download: TenantUserPermission.AccessRuleBuilderRulesDownload,
        products: [TenantProductName.DfpaDemandPlanning],
      },
    ],
  },
  {
    label: 'Cue',
    areas: [
      {
        label: 'Cue',
        description: 'This permission grants access to the Cue page and the chat assistant',
        view: TenantUserPermission.AccessChatDataView,
        download: TenantUserPermission.AccessChatDataDownload,
        products: [TenantProductName.ChatWithData],
      },
    ],
  },
  {
    label: 'Monitoring',
    areas: [
      {
        label: 'Monitoring',
        description: 'This permission grants access to the Audit Logs, Export Jobs, and Sync Status pages',
        view: TenantUserPermission.AccessSettingsMonitoringView,
        download: TenantUserPermission.AccessSettingsMonitoringDownload,
      },
      {
        label: 'MCP Sessions',
        description: 'This permission grants access to the MCP Sessions page',
        view: TenantUserPermission.AccessSettingsMcpSessionsView,
        products: [TenantProductName.Mcp],
      },
    ],
  },
  {
    label: 'Settings',
    areas: [
      {
        label: 'General',
        description:
          'This permission grants access to the Settings, Location Settings, Replenishment Paths, and Update Credentials pages',
        view: TenantUserPermission.AccessSettingsGeneralView,
      },
      {
        label: 'Users',
        description: 'This permission grants access to the Users page',
        view: TenantUserPermission.AccessSettingsUsersView,
      },
    ],
  },
];

const allAccessAreas = accessSections.flatMap((section) => section.areas);

interface ActionPermission {
  label: string;
  permission: TenantUserPermission;
  description: string;
  products?: TenantProductName[];
}

const actionPermissions: ActionPermission[] = [
  {
    label: 'Edit Item & Supplier Details',
    permission: TenantUserPermission.ActionEditItemSupplierDetails,
    products: [TenantProductName.DfpaDemandPlanning, TenantProductName.DfpaPurchasingAutomation],
    description:
      'This permission allows editing item and supplier settings other than min/max, stock inheritance, and marking locations as reviewed',
  },
  {
    label: 'Export Min/Max',
    permission: TenantUserPermission.ActionExportMinMax,
    products: [TenantProductName.DfpaDemandPlanning],
    description: 'This permission allows exporting min/maxes to the ERP, including Daily Workflow submissions',
  },
  {
    label: 'Export Purchase & Transfer Orders',
    permission: TenantUserPermission.ActionExportPurchaseTransferOrders,
    products: [TenantProductName.DfpaPurchasingAutomation],
    description: 'This permission allows building and submitting purchase and transfer orders to the ERP',
  },
  {
    label: 'Edit Forecast & Usage',
    permission: TenantUserPermission.ActionEditForecastUsage,
    products: [TenantProductName.DfpaDemandPlanning],
    description: 'This permission allows editing forecast and usage overrides, including usage inheritance',
  },
  {
    label: 'Edit Settings',
    permission: TenantUserPermission.ActionEditSettings,
    description:
      'This permission allows editing tenant, location, location transfer, and ERP connection settings, and replenishment path rules',
  },
  {
    label: 'Modify Users',
    permission: TenantUserPermission.ActionModifyUsers,
    description: 'This permission allows inviting users and managing their access',
  },
  {
    label: 'Manage Tasks',
    permission: TenantUserPermission.ActionManageTasks,
    products: [TenantProductName.SalesCrm],
    description: 'This permission allows creating, editing, completing, and deleting tasks',
  },
  {
    label: 'Create Rules',
    permission: TenantUserPermission.ActionCreateRules,
    products: [TenantProductName.DfpaDemandPlanning],
    description: 'This permission allows creating, editing, deploying, and deleting rules and templates',
  },
  {
    label: 'Create Purchase Groups',
    permission: TenantUserPermission.ActionCreatePurchaseGroups,
    products: [TenantProductName.DfpaPurchasingAutomation],
    description: 'This permission allows creating, editing, and deleting purchase groups',
  },
  {
    label: 'MCP Read',
    permission: TenantUserPermission.ActionMcpRead,
    products: [TenantProductName.Mcp],
    description: 'This permission allows reading ERP data from AI tools through the Recurrency MCP server',
  },
  {
    label: 'MCP Write',
    permission: TenantUserPermission.ActionMcpWrite,
    products: [TenantProductName.Mcp],
    description: 'This permission allows reading and writing ERP data through the Recurrency MCP server',
  },
];

export enum PermissionPreset {
  StandardUser = 'Standard User',
  StandardAi = 'Standard + AI',
  TenantAdmin = 'Tenant Admin',
}

/** Standard User and Tenant Admin are kept in step with the backfill_permissions scripts in db-schemas. */
const standardUserPreset: TenantUserPermission[] = [
  TenantUserPermission.AccessPlanningDashboardDownload,
  TenantUserPermission.AccessPlanningDailyWorkflowView,
  TenantUserPermission.AccessPlanningForecastingDownload,
  TenantUserPermission.AccessPlanningPlanningDownload,
  TenantUserPermission.AccessPurchasingPurchaseTargetsView,
  TenantUserPermission.AccessPurchasingTransferReplenishmentView,
  TenantUserPermission.AccessPurchasingPurchaseGroupsView,
  TenantUserPermission.AccessPurchasingPurchaseOrdersView,
  TenantUserPermission.AccessPurchasingTransferOrdersView,
  TenantUserPermission.AccessPurchasingPurchaseStatusDownload,
  TenantUserPermission.AccessPurchasingVendorsDownload,
  TenantUserPermission.AccessPurchasingInventoryView,
  TenantUserPermission.AccessSalesDashboardView,
  TenantUserPermission.AccessSalesCustomersView,
  TenantUserPermission.AccessOrdersQuotesView,
  TenantUserPermission.AccessOrdersOrdersView,
  TenantUserPermission.AccessReportingReportingDownload,
  TenantUserPermission.AccessTasksTasksDownload,
  TenantUserPermission.ActionEditItemSupplierDetails,
  TenantUserPermission.ActionExportMinMax,
  TenantUserPermission.ActionExportPurchaseTransferOrders,
  TenantUserPermission.ActionEditForecastUsage,
  TenantUserPermission.ActionManageTasks,
  TenantUserPermission.ActionCreatePurchaseGroups,
];

const presetPermissions: Record<PermissionPreset, TenantUserPermission[]> = {
  [PermissionPreset.StandardUser]: standardUserPreset,
  [PermissionPreset.StandardAi]: [
    ...standardUserPreset,
    TenantUserPermission.AccessChatDataDownload,
    TenantUserPermission.ActionMcpWrite,
  ],
  [PermissionPreset.TenantAdmin]: [
    ...standardUserPreset,
    TenantUserPermission.AccessRuleBuilderRulesDownload,
    TenantUserPermission.AccessSettingsGeneralView,
    TenantUserPermission.AccessSettingsUsersView,
    TenantUserPermission.AccessSettingsMcpSessionsView,
    TenantUserPermission.AccessSettingsMonitoringDownload,
    TenantUserPermission.ActionEditSettings,
    TenantUserPermission.ActionModifyUsers,
    TenantUserPermission.ActionCreateRules,
  ],
};

/** A granted `.download` implies its `.view`, so only the download is stored. */
export const storedPermissions = (permissions: PermissionValues | undefined): TenantUserPermission[] => {
  // permissions removed from the enum since the user was last saved are dropped because the endpoint rejects them
  const granted = new Set(
    Object.entries(permissions ?? {})
      .filter(([_perm, isGranted]) => isGranted)
      .map(([permission]) => permission as TenantUserPermission)
      .filter((permission) => Object.values(TenantUserPermission).includes(permission)),
  );
  for (const { view, download } of allAccessAreas) {
    if (download && granted.has(download)) {
      // Delete download if view field is not checked. Delete view before upload since download implies view.
      granted.delete(granted.has(view) ? view : download);
    }
  }
  // MCP write implies read the same way, so only the write is stored
  if (granted.has(TenantUserPermission.ActionMcpWrite)) {
    granted.delete(TenantUserPermission.ActionMcpRead);
  }
  return [...granted];
};

/** Re-checks the box that a stored grant implies but does not itself record: `.view` for a `.download`, MCP read for MCP write. */
export const permissionFormValues = (permissions: TenantUserPermission[] | undefined): PermissionValues =>
  Object.fromEntries(
    (permissions ?? []).flatMap((permission) => {
      if (permission === TenantUserPermission.ActionMcpWrite) {
        return [
          [permission, true],
          [TenantUserPermission.ActionMcpRead, true],
        ];
      }
      // Find area where download permission is granted
      const area = allAccessAreas.find(({ download }) => download === permission);
      return area
        ? [
            [permission, true],
            [area.view, true],
          ]
        : [[permission, true]];
    }),
  );

/** Resolves false when the editor backs out of saving a user who would be left with no access at all. */
export async function confirmSaveWithoutPermissions(permissions: PermissionValues | undefined): Promise<boolean> {
  if (storedPermissions(permissions).length > 0) {
    return true;
  }
  const didConfirm = await showAsyncModal(ConfirmModal, {
    title: 'Continue without permissions?',
    content:
      'This user has no permissions and will not be able to open any page or take any action. Do you wish to continue?',
    okText: 'Yes',
    width: 480,
  });
  return Boolean(didConfirm);
}

const permissionPanelsClassName = css`
  & > .ant-collapse-item > .ant-collapse-header {
    padding: 8px 0;
    font-weight: ${fontWeights.medium};
    color: ${colors.neutral[600]};
  }
  & > .ant-collapse-item > .ant-collapse-content > .ant-collapse-content-box {
    padding: 0 0 12px;
  }
`;

type Capability = 'view' | 'download';

export function PermissionsSection({
  value,
  onChange,
  tenant,
  canGrantSettings,
  defaultPreset = PermissionPreset.StandardUser,
}: {
  value: PermissionValues;
  onChange: (next: PermissionValues) => void;
  /** When given, rows for products this tenant does not show are hidden; their grants are left untouched. */
  tenant?: TenantDTO;
  /** Whether the Settings rows and the Tenant Admin preset are offered; an editor can only hand out what they hold. */
  canGrantSettings: boolean;
  defaultPreset?: PermissionPreset;
}) {
  const [selectedPreset, setSelectedPreset] = useState(defaultPreset);

  const isOfferedToTenant = ({ products }: { products?: TenantProductName[] }) =>
    tenant === undefined || products === undefined || products.some((product) => shouldShowProduct(tenant, product));
  const offeredAccessSections = accessSections
    .map((section) => ({
      ...section,
      areas: section.areas.filter(
        (area) =>
          isOfferedToTenant(area) && (canGrantSettings || area.view !== TenantUserPermission.AccessSettingsGeneralView),
      ),
    }))
    .filter((section) => section.areas.length > 0);
  const offeredPresets = Object.values(PermissionPreset).filter(
    (preset) => canGrantSettings || preset !== PermissionPreset.TenantAdmin,
  );
  const offeredActions = actionPermissions.filter(
    (action) =>
      isOfferedToTenant(action) && (canGrantSettings || action.permission !== TenantUserPermission.ActionEditSettings),
  );

  /** Sets the rows this editor can see to exactly the preset; hidden rows keep their grants. */
  const applyPreset = (preset: TenantUserPermission[]) => {
    const offered = [
      ...offeredAccessSections.flatMap(({ areas }) =>
        areas.flatMap(({ view, download }) => (download ? [view, download] : [view])),
      ),
      ...offeredActions.map(({ permission }) => permission),
    ];
    const granted = permissionFormValues(preset);
    const next = { ...value };
    for (const permission of offered) {
      next[permission] = granted[permission] ?? false;
    }
    onChange(next);
  };

  const capabilityBoxes = (section: AccessSection, capability: Capability): TenantUserPermission[] =>
    section.areas
      .map((area) => (capability === 'view' ? area.view : area.download))
      .filter((permission): permission is TenantUserPermission => permission !== undefined);

  /**
   * Toggling a whole column keeps the download-implies-view invariant: granting a download grants its
   * view, and clearing a view clears the download that cannot outlive it.
   */
  const setCapability = (section: AccessSection, capability: Capability, isGranted: boolean) => {
    const next = { ...value };
    for (const area of section.areas) {
      if (capability === 'view') {
        next[area.view] = isGranted;
        if (!isGranted && area.download) {
          next[area.download] = false;
        }
      } else if (area.download) {
        next[area.download] = isGranted;
        if (isGranted) {
          next[area.view] = true;
        }
      }
    }
    onChange(next);
  };

  const capabilityHeader = (section: AccessSection, capability: Capability, label: string) => {
    const boxes = capabilityBoxes(section, capability);
    if (section.areas.length < 2 || boxes.length === 0) {
      return label;
    }
    const grantedCount = boxes.filter((permission) => value[permission]).length;
    return (
      <Checkbox
        checked={grantedCount === boxes.length}
        indeterminate={grantedCount > 0 && grantedCount < boxes.length}
        onChange={(event) => setCapability(section, capability, event.target.checked)}
      >
        {label}
      </Checkbox>
    );
  };

  /** MCP write implies read, the way a download implies its view. */
  const setAction = (permission: TenantUserPermission, isGranted: boolean) => {
    const next = { ...value, [permission]: isGranted };
    if (permission === TenantUserPermission.ActionMcpWrite && isGranted) {
      next[TenantUserPermission.ActionMcpRead] = true;
    }
    if (permission === TenantUserPermission.ActionMcpRead && !isGranted) {
      next[TenantUserPermission.ActionMcpWrite] = false;
    }
    onChange(next);
  };

  const actionsHeader = () => {
    const grantedCount = offeredActions.filter(({ permission }) => value[permission]).length;
    return (
      <Checkbox
        checked={offeredActions.length > 0 && grantedCount === offeredActions.length}
        indeterminate={grantedCount > 0 && grantedCount < offeredActions.length}
        onChange={(event) => {
          const next = { ...value };
          for (const { permission } of offeredActions) {
            next[permission] = event.target.checked;
          }
          onChange(next);
        }}
      >
        Enabled
      </Checkbox>
    );
  };

  const actionColumns: ColumnType<ActionPermission>[] = [
    {
      title: 'Permission',
      dataIndex: asKeyOf<ActionPermission>('label'),
      width: '100%',
      render: (label, action) => (
        <InfoTooltip title={action.description} placement="right" useInfoIcon>
          {label}
        </InfoTooltip>
      ),
    },
    {
      title: actionsHeader(),
      align: 'center',
      width: '128px',
      render: (_label, action) => (
        <Checkbox
          checked={Boolean(value[action.permission])}
          onChange={(event) => setAction(action.permission, event.target.checked)}
        />
      ),
    },
  ];

  const accessColumnsFor = (section: AccessSection): ColumnType<AccessArea>[] => [
    {
      title: 'Area',
      dataIndex: asKeyOf<AccessArea>('label'),
      width: '100%',
      render: (label, area) => (
        <InfoTooltip title={area.description} placement="right" useInfoIcon>
          {label}
        </InfoTooltip>
      ),
    },
    {
      title: capabilityHeader(section, 'view', 'View'),
      align: 'center',
      width: '128px',
      render: (_label, area) => (
        <Checkbox
          checked={Boolean(value[area.view])}
          onChange={(event) => onChange({ ...value, [area.view]: event.target.checked })}
        />
      ),
    },
    {
      title: capabilityHeader(section, 'download', 'Download'),
      align: 'center',
      width: '150px',
      render: (_label, area) =>
        area.download && (
          <Checkbox
            checked={Boolean(value[area.download])}
            disabled={!value[area.view]}
            onChange={(event) => onChange({ ...value, [area.download as TenantUserPermission]: event.target.checked })}
          />
        ),
    },
  ];

  return (
    <>
      <div
        className={css`
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 8px;
        `}
      >
        <span
          className={css`
            color: ${colors.neutral[600]};
          `}
        >
          Presets
        </span>
        <Select
          className={css`
            width: 180px;
          `}
          value={selectedPreset}
          onChange={setSelectedPreset}
          options={offeredPresets.map((preset) => ({ value: preset }))}
        />
        <Button size="small" onClick={() => applyPreset(presetPermissions[selectedPreset])}>
          Apply
        </Button>
      </div>
      <Collapse
        ghost
        defaultActiveKey={[...offeredAccessSections.map((section) => section.label), 'Actions']}
        className={permissionPanelsClassName}
      >
        {offeredAccessSections.map((section) => (
          <Collapse.Panel key={section.label} header={section.label}>
            <Table data={section.areas} columns={accessColumnsFor(section)} />
          </Collapse.Panel>
        ))}
        <Collapse.Panel key="Actions" header="Actions">
          <Table data={offeredActions} columns={actionColumns} />
        </Collapse.Panel>
      </Collapse>
    </>
  );
}
